import { httpsCallable } from 'firebase/functions'
import { functions } from './firebase'

/**
 * Normalize any error to a plain object. Use this before reporting so we never
 * pass frozen or SDK error objects by reference (avoids "Cannot add property code,
 * object is not extensible" when Firestore or other code touches the same object).
 */
export function normalizeErrorForReporting(error) {
  if (error == null) return { message: 'Unknown error', stack: '', code: '' }
  try {
    // HARDENING: Never read `error.stack`.
    // In some environments/SDKs, `stack` can be exposed via a getter/proxy that throws
    // "Cannot redefine property: stack", and since this is only for reporting,
    // we prefer safe reporting over stack traces.
    const stack = ''
    return {
      message: typeof error.message === 'string' ? error.message : String(error?.message ?? error ?? 'Unknown error'),
      stack,
      code: error.code != null ? String(error.code) : '',
    }
  } catch (_) {
    return { message: String(error), stack: '', code: '' }
  }
}

/** Origins from which error report emails are sent. No emails from localhost or other domains. */
const ALLOWED_ERROR_REPORT_ORIGINS = [
  'https://primedocumentsign.web.app',
  'https://primedocumentsign.com',
]

function isAllowedOriginForErrorReport() {
  if (typeof window === 'undefined' || !window.location?.origin) return false
  const origin = window.location.origin.replace(/\/$/, '')
  return ALLOWED_ERROR_REPORT_ORIGINS.includes(origin)
}

/** Derive a friendly traffic source from document.referrer (e.g. TikTok, Facebook, Google). */
function getReferrerSource(referrer) {
  if (!referrer || typeof referrer !== 'string') return 'Direct'
  try {
    const host = new URL(referrer).hostname.toLowerCase()
    if (host.includes('tiktok.com')) return 'TikTok'
    if (host.includes('facebook.com') || host.includes('fb.com') || host.includes('fb.me')) return 'Facebook'
    if (host.includes('instagram.com')) return 'Instagram'
    if (host.includes('twitter.com') || host.includes('x.com')) return 'Twitter / X'
    if (host.includes('google.') || host.includes('google.com')) return 'Google'
    if (host.includes('bing.com')) return 'Bing'
    if (host.includes('linkedin.com')) return 'LinkedIn'
    if (host.includes('youtube.com')) return 'YouTube'
    if (host.includes('whatsapp')) return 'WhatsApp'
    if (host.includes('telegram')) return 'Telegram'
    if (host.includes('snapchat')) return 'Snapchat'
    if (host.includes('pinterest')) return 'Pinterest'
    return host || 'Other'
  } catch (_) {
    return 'Direct'
  }
}

/** Parse userAgent for device type, OS, and browser. */
function parseDeviceContext(userAgent) {
  const ua = typeof userAgent === 'string' ? userAgent : (typeof navigator !== 'undefined' ? navigator?.userAgent || '' : '')
  let deviceType = 'desktop'
  let os = ''
  let browser = ''
  if (/mobile|android|webos|iphone|ipod|blackberry|iemobile|opera mini/i.test(ua)) {
    deviceType = /ipad|tablet|playbook|silk|kindle/i.test(ua) ? 'tablet' : 'mobile'
  }
  if (/android/i.test(ua)) os = 'Android'
  else if (/iphone|ipad|ipod/i.test(ua)) os = 'iOS'
  else if (/windows phone/i.test(ua)) os = 'Windows Phone'
  else if (/windows/i.test(ua)) os = 'Windows'
  else if (/mac os x|macintosh/i.test(ua)) os = 'macOS'
  else if (/cros/i.test(ua)) os = 'Chrome OS'
  else if (/linux/i.test(ua)) os = 'Linux'
  if (/edg\//i.test(ua)) browser = 'Edge'
  else if (/opr\//i.test(ua) || /opera/i.test(ua)) browser = 'Opera'
  else if (/chrome/i.test(ua) && !/edg|opr/i.test(ua)) browser = 'Chrome'
  else if (/safari/i.test(ua) && !/chrome/i.test(ua)) browser = 'Safari'
  else if (/firefox/i.test(ua)) browser = 'Firefox'
  else if (/samsungbrowser/i.test(ua)) browser = 'Samsung Internet'
  if (!browser) browser = 'Other'
  if (!os) os = 'Other'
  return { deviceType, os, browser }
}

/** Fetch approximate location (country, city, region) from IP. Fire-and-forget friendly; 2s timeout. */
function fetchGeoContext() {
  return Promise.race([
    fetch('https://ip-api.com/json/?fields=country,city,regionName', { method: 'GET' })
      .then((r) => (r.ok ? r.json() : {}))
      .then((d) => ({
        country: d.country || '',
        city: d.city || '',
        regionName: d.regionName || '',
      }))
      .catch(() => ({})),
    new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 2000)),
  ]).catch(() => ({}))
}

function getViewportContext() {
  if (typeof window === 'undefined') return {}
  return {
    viewportWidth: window.innerWidth ?? null,
    viewportHeight: window.innerHeight ?? null,
    devicePixelRatio: window.devicePixelRatio ?? null,
  }
}

function getPageContextFromDom() {
  try {
    if (typeof document === 'undefined' || typeof window === 'undefined') return {}

    const pageTitle = document.title || ''
    const path = window.location?.pathname || ''
    const search = window.location?.search || ''
    const hash = window.location?.hash || ''

    const pageEl = document.querySelector('.page')
    const pageClass = pageEl?.className ? String(pageEl.className) : ''

    const headingCandidates = [
      'h1',
      'h2',
      'h3',
      '.hero-card-title',
      '.preview-modal__header h3',
      '.document-section-header__title span',
    ]
    let visibleHeading = ''
    for (const sel of headingCandidates) {
      const el = document.querySelector(sel)
      const txt = (el?.textContent || '').trim()
      if (txt) {
        visibleHeading = txt
        break
      }
    }
    if (visibleHeading.length > 140) visibleHeading = visibleHeading.slice(0, 140) + '…'

    // Short "what are they looking at" hint using the first couple of sentences from a paragraph.
    let visibleSubheading = ''
    const p = document.querySelector('.preview-modal__subtitle, p')
    if (p?.textContent) {
      visibleSubheading = p.textContent.trim().replace(/\s+/g, ' ')
      if (visibleSubheading.length > 180) visibleSubheading = visibleSubheading.slice(0, 180) + '…'
    }

    return {
      pageTitle,
      pagePath: path,
      pageQuery: search,
      pageHash: hash,
      pageClass,
      visibleHeading,
      visibleSubheading,
    }
  } catch (_) {
    return {}
  }
}

/**
 * Fire-and-forget: report error to admin email (kdprodc@gmail.com).
 * Includes device type, OS, browser, referrer/source (e.g. TikTok), and optional location (country, city).
 * Only sends from production: https://primedocumentsign.web.app and https://primedocumentsign.com.
 * No error emails from localhost or other origins. Does not throw.
 * Accepts an Error or a plain object from normalizeErrorForReporting.
 */
export function reportErrorToAdmin(source, error, details = {}) {
  try {
    if (!isAllowedOriginForErrorReport()) {
      return // skip sending error emails from localhost and non-production origins
    }
    const normalized = normalizeErrorForReporting(error)
    const userAgent = typeof navigator !== 'undefined' ? navigator?.userAgent || '' : ''
    const referrer = typeof document !== 'undefined' ? document?.referrer || '' : ''
    const { deviceType, os, browser } = parseDeviceContext(userAgent)
    const referrerSource = getReferrerSource(referrer)
    const geoContext = getPageContextFromDom()
    const basePayload = {
      source,
      message: normalized.message || 'Unknown error',
      stack: normalized.stack || '',
      code: normalized.code || '',
      details,
      url: typeof window !== 'undefined' ? window.location?.href || '' : '',
      userAgent,
      referrer: referrer || undefined,
      referrerSource,
      deviceType,
      os,
      browser,
      ...geoContext,
      ...getViewportContext(),
    }
    const reportError = httpsCallable(functions, 'reportError')
    ;(async () => {
      let geo = {}
      try {
        geo = await fetchGeoContext()
      } catch (_) {}
      reportError({ ...basePayload, ...geo }).catch((e) => console.error('Failed to report error to admin:', e))
    })()
  } catch (e) {
    console.error('Failed to report error to admin:', e)
  }
}
